import type { AutomationResult, AutomationTask } from '../types'
import type { IAutomationProvider } from '../browser/IAutomationProvider'
import { StubAutomationProvider } from '../strategies/StubAutomationProvider'

export type WorkerHandler = (task: AutomationTask, provider: IAutomationProvider) => Promise<AutomationResult>

/**
 * WorkerPool — bounded concurrency for automation tasks.
 */
export class WorkerPool {
  private active = 0
  private maxWorkers: number
  private timeoutMs: number
  private readonly running = new Set<string>()

  constructor(maxWorkers = 3, timeoutMs = 15_000) {
    this.maxWorkers = maxWorkers
    this.timeoutMs = timeoutMs
  }

  configure(options: { maxWorkers?: number; timeoutMs?: number }): void {
    if (options.maxWorkers != null) this.maxWorkers = Math.max(1, options.maxWorkers)
    if (options.timeoutMs != null) this.timeoutMs = Math.max(100, options.timeoutMs)
  }

  getStats() {
    return {
      maxWorkers: this.maxWorkers,
      active: this.active,
      available: Math.max(0, this.maxWorkers - this.active),
      timeoutMs: this.timeoutMs,
      runningIds: [...this.running],
    }
  }

  canAccept(): boolean {
    return this.active < this.maxWorkers
  }

  async run(
    task: AutomationTask,
    handler: WorkerHandler,
    providerFactory: (supplier: string) => IAutomationProvider = (code) =>
      new StubAutomationProvider(code),
  ): Promise<AutomationResult> {
    if (!this.canAccept()) {
      return {
        success: false,
        executionTime: 0,
        itemsFound: 0,
        warnings: [],
        errors: ['WorkerPool cheio'],
        logs: [],
      }
    }

    this.active += 1
    this.running.add(task.id)
    const provider = providerFactory(task.supplier)
    const started = performance.now()

    try {
      const result = await Promise.race([
        handler(task, provider),
        rejectAfter(this.timeoutMs),
      ])
      return result
    } catch (error) {
      return {
        success: false,
        executionTime: Math.round(performance.now() - started),
        itemsFound: 0,
        warnings: [],
        errors: [error instanceof Error ? error.message : 'Falha no worker'],
        logs: [],
      }
    } finally {
      this.active = Math.max(0, this.active - 1)
      this.running.delete(task.id)
      try {
        await provider.close()
      } catch {
        /* ignore */
      }
    }
  }
}

function rejectAfter(ms: number): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`Worker timeout após ${ms}ms`)), ms)
  })
}

export const workerPool = new WorkerPool()
